import { formatQuantity } from '../../utils/inventoryLabels';
import { matchCodes, sameSlotCode } from '../operator/operatorCodes';
import { asSentence } from '../operator/operatorUtils';

/*
 * The rules of the kit station: how many can be made or taken apart, where
 * each component comes from, what a write sends and what to say once it went.
 * Quantities come with up to three decimals: they are summed in thousandths,
 * so floats do not invent a remainder.
 */

export const KIT_WORK = { ASSEMBLE: 'assemble', DISASSEMBLE: 'disassemble' };

export const KIT_WORK_LABELS = { [KIT_WORK.ASSEMBLE]: 'Armar', [KIT_WORK.DISASSEMBLE]: 'Desarmar' };

/** A visit's steps: how many → where they come from → where they go → how it went. */
export const KIT_STEP = { QUANTITY: 'quantity', SOURCES: 'sources', DESTINATION: 'destination', RESULT: 'result' };

const milli = (value) => Math.round((Number(value) || 0) * 1000);
const fromMilli = (value) => value / 1000;

const isDisassembly = (mode) => mode === KIT_WORK.DISASSEMBLE;

const plural = (quantity, one, many) => (Number(quantity) === 1 ? one : many);

/** «2 KIT-1». */
const countText = (quantity, sku) => `${formatQuantity(quantity)} ${sku}`;

/** «POL-1», «POL-1 y GOR-1», «POL-1, GOR-1 y TAZ-1». */
export const listText = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}` : items[0] || '';

/** A kit as the floor names it: its SKU. */
export const kitName = (kit) => kit?.sku || (kit?.id != null ? `Kit #${kit.id}` : 'Kit');

/** The kits of the station a code is: by barcode, SKU or a pack's barcode, as the server reads them. */
export const matchKits = (rows, code) => (rows || []).filter((row) => matchCodes(row.codes, code));

/** The kits free to take apart: the stock no order reserved. */
export const kitFree = (kit) => Math.max(Number(kit?.kit_stock?.free) || 0, 0);

/** How many the mode can do, as the kit was read: put together, or take apart. */
export const kitModeMax = (kit, mode) =>
  isDisassembly(mode) ? kitFree(kit) : Math.max(Number(kit?.can_assemble) || 0, 0);

/** «Falta POL-1», «Faltan POL-1 y GOR-1». */
const missingText = (skus) => (skus.length > 0 ? `${skus.length > 1 ? 'Faltan' : 'Falta'} ${listText(skus)}` : 'No alcanza');

/** A kit of the list: «Arma hasta 3 · 4 en stock», or what it lacks to be made. */
export const kitRowCaption = (row) => {
  const can = kitModeMax(row, KIT_WORK.ASSEMBLE);
  const head = can > 0 ? `Arma hasta ${formatQuantity(can)}` : missingText(row.limited_by || []);
  return `${head} · ${formatQuantity(kitFree(row))} en stock`;
};

/**
 * How many the mode can do and why not more: «Puedes armar hasta 3 · lo
 * limita POL-1», «No alcanza para armar: falta POL-1», «Puedes desarmar hasta
 * 4», «No hay KIT-1 libres para desarmar».
 */
export const kitMaxText = (kit, mode) => {
  const max = kitModeMax(kit, mode);
  if (isDisassembly(mode)) {
    return max > 0 ? `Puedes desarmar hasta ${formatQuantity(max)}` : `No hay ${kitName(kit)} libres para desarmar`;
  }
  const limited = kit?.limited_by || [];
  if (max <= 0) {
    return limited.length > 0
      ? `No alcanza para armar: ${limited.length > 1 ? 'faltan' : 'falta'} ${listText(limited)}`
      : 'No alcanza para armar';
  }
  const by = limited.length > 0 ? ` · ${limited.length > 1 ? 'lo limitan' : 'lo limita'} ${listText(limited)}` : '';
  return `Puedes armar hasta ${formatQuantity(max)}${by}`;
};

/** Why the kit cannot be taken apart in this version, or `null`: «No se desarma en esta versión: LEC-1 controla lote.» */
export const kitBlockedText = (kit) => {
  if (!kit?.disassemble_blocked) return null;
  const skus = (kit.components || []).filter((component) => component.lot_controlled).map((component) => component.sku);
  if (skus.length === 0) return 'No se desarma en esta versión.';
  return `No se desarma en esta versión: ${listText(skus)} ${skus.length > 1 ? 'controlan' : 'controla'} lote.`;
};

/**
 * Why a quantity cannot go on, written for the button: «Falta POL-1 para
 * armar», «Indica cuántos armas», «Se arma en unidades enteras», «Puedes armar
 * hasta 3», «Hay 3 para desarmar». `null` when it can.
 *
 * @param {string|number} quantity
 * @param {object|null} kit - As `GET kits/<id>/` sends it.
 * @param {string} mode
 */
export const kitQuantityProblem = (quantity, kit, mode) => {
  const disassembly = isDisassembly(mode);
  if (disassembly && kit?.disassemble_blocked) return 'No se desarma en esta versión';
  const max = kitModeMax(kit, mode);
  if (max <= 0) return disassembly ? `No hay ${kitName(kit)} para desarmar` : `${missingText(kit?.limited_by || [])} para armar`;
  const amount = Number(quantity);
  if (!(amount > 0)) return disassembly ? 'Indica cuántos desarmas' : 'Indica cuántos armas';
  if (!kit?.allows_fractional_qty && !Number.isInteger(amount)) {
    return disassembly ? 'Se desarma en unidades enteras' : 'Se arma en unidades enteras';
  }
  if (milli(amount) > milli(max)) {
    return disassembly ? `Hay ${formatQuantity(max)} para desarmar` : `Puedes armar hasta ${formatQuantity(max)}`;
  }
  return null;
};

/** What a plan is asked with: the kit, the mode and the quantity on screen. */
export const planArgOf = ({ kitId, mode, quantity }) => ({ kitId, mode, quantity: Number(quantity) });

/** A place stock can come from, as a key: its slot and its lot. */
export const sourceKeyOf = ({ slot_code: slotCode, lot }) => `${slotCode}|${lot || ''}`;

const legOf = (place, quantity) => ({
  slotCode: place.slot_code,
  slotName: place.slot_name || place.slot_code,
  lot: place.lot || '',
  expiryDate: place.expiry_date || null,
  quantity,
});

/**
 * Where each component comes from: the plan's legs, or — for a component the
 * operator changed (`preferred`, by product id, a `sourceKeyOf`) — that place
 * first and then the plan's others in its order, never past what is free.
 * `short` is what the legs leave missing.
 *
 * @param {object|null} plan - `GET kits/<id>/plan/?mode=assemble`.
 * @param {Record<number, string>} [preferred]
 * @returns {Array<{productId: number, sku: string, needed: number, short: number, chosen: boolean,
 *   legs: Array<{slotCode: string, slotName: string, lot: string, expiryDate: string|null, quantity: number}>,
 *   options: object[]}>}
 */
export const planLegs = (plan, preferred = {}) =>
  (plan?.components || []).map((component) => {
    const options = component.options || [];
    const choice = preferred[component.product_id];
    const first = choice ? options.find((option) => sourceKeyOf(option) === choice) : null;
    let legs;
    if (first) {
      let left = Math.min(milli(component.needed), milli(component.free));
      legs = [];
      [first, ...options.filter((option) => option !== first)].forEach((option) => {
        const take = Math.min(left, milli(option.in_slot));
        if (take <= 0) return;
        legs.push(legOf(option, fromMilli(take)));
        left -= take;
      });
    } else {
      legs = (component.legs || []).map((leg) => legOf(leg, Number(leg.quantity)));
    }
    const taken = legs.reduce((total, leg) => total + milli(leg.quantity), 0);
    return {
      productId: component.product_id,
      sku: component.sku,
      needed: Number(component.needed),
      short: fromMilli(Math.max(milli(component.needed) - taken, 0)),
      chosen: Boolean(first),
      legs,
      options,
    };
  });

/**
 * Where the kits being taken apart come out of: the place the operator chose
 * (`preferred[kit id]`), or the plan's. `null` when there is none.
 *
 * @param {object|null} plan - `GET kits/<id>/plan/?mode=disassemble`.
 * @param {Record<number, string>} [preferred]
 */
export const disassemblySource = (plan, preferred = {}) => {
  const options = plan?.options || [];
  const kitId = plan?.kit?.id;
  const chosen = kitId != null && preferred[kitId] ? options.find((option) => sourceKeyOf(option) === preferred[kitId]) : null;
  const source = chosen || plan?.source;
  if (!source) return null;
  const listed = chosen || options.find((option) => sourceKeyOf(option) === sourceKeyOf(source));
  return {
    slotCode: source.slot_code,
    slotName: listed?.slot_name || source.slot_code,
    lot: source.lot || '',
    inSlot: Number(source.in_slot) || 0,
    chosen: Boolean(chosen),
  };
};

/** Why the sources on screen cannot be confirmed, written for the button: «Falta 1 de GOR-1». `null` when they can. */
export const kitSourcesProblem = (plan, preferred, mode, quantity) => {
  if (!plan) return 'Calculando de dónde sale';
  if (isDisassembly(mode)) {
    const source = disassemblySource(plan, preferred);
    if (!source) return `No hay ${kitName(plan.kit)} para desarmar`;
    return milli(quantity) > milli(source.inSlot) ? `En ${source.slotCode} hay ${formatQuantity(source.inSlot)}` : null;
  }
  const short = planLegs(plan, preferred).find((component) => component.short > 0);
  return short ? `${plural(short.short, 'Falta', 'Faltan')} ${formatQuantity(short.short)} de ${short.sku}` : null;
};

/**
 * What the sources lack, said in place: «Ya no alcanza para armar 3 KIT-1:
 * puedes armar hasta 2.», «En P-01 hay 2 KIT-1: elige otra ubicación o desarma
 * menos.» `null` when nothing is missing.
 */
export const kitPlanShortText = (plan, preferred, mode, quantity) => {
  if (!plan || !kitSourcesProblem(plan, preferred, mode, quantity)) return null;
  const sku = kitName(plan.kit);
  if (isDisassembly(mode)) {
    const source = disassemblySource(plan, preferred);
    return source
      ? `En ${source.slotCode} hay ${countText(source.inSlot, sku)}: elige otra ubicación o desarma menos.`
      : `No hay ${sku} en una ubicación de la que se pueda sacar.`;
  }
  const max = Number(plan.max_quantity) || 0;
  return max > 0
    ? `Ya no alcanza para armar ${countText(quantity, sku)}: puedes armar hasta ${formatQuantity(max)}.`
    : `Ya no alcanza para armar ${countText(quantity, sku)}.`;
};

/**
 * The destination a scanned code is, when it is one the plan suggests.
 *
 * @returns {{code: string, name: string, suggested: true}|null}
 */
export const suggestedKitDestination = (plan, code) => {
  const hit = (plan?.destinations || []).find((item) => sameSlotCode(item.slot_code, code));
  return hit ? { code: hit.slot_code, name: hit.slot_name || hit.slot_code, suggested: true } : null;
};

/** Why the destination step cannot send, written for the button; `null` when it can. */
export const kitDestinationProblem = ({ kit, mode, destination, kitLot }) => {
  if (!destination) return isDisassembly(mode) ? 'Escanea dónde dejas los componentes' : 'Escanea dónde dejas los kits';
  if (!isDisassembly(mode) && kit?.lot_controlled && !kitLot.lot.trim()) return 'Falta el lote';
  if (!isDisassembly(mode) && kit?.expiry_controlled && !kitLot.expiryDate) return 'Falta el vencimiento';
  return null;
};

/** The first reason the visit cannot send its write, from the quantity on; `null` when it can. */
export const kitWriteProblem = (visit) =>
  kitQuantityProblem(visit.quantity, visit.kit, visit.mode) ||
  kitSourcesProblem(visit.plan, visit.preferred, visit.mode, visit.quantity) ||
  kitDestinationProblem(visit);

/**
 * The write the visit sends, as the operator meant it (the key is the
 * thunk's): the plan's legs with what the operator changed, or the place the
 * kits come out of. Built the same way every time, so the same action sent
 * again after a lost answer goes under its first key. `null` while something
 * is missing.
 *
 * @param {object} visit - The `operatorKits` slice.
 */
export const kitWriteArgOf = (visit) => {
  const { kit, mode, quantity, plan, preferred, destination, kitLot } = visit;
  if (!kit || kitWriteProblem(visit)) return null;
  const base = { kitId: kit.id, kitSku: kitName(kit), quantity: Number(quantity) };
  if (isDisassembly(mode)) {
    const source = disassemblySource(plan, preferred);
    return { ...base, fromSlotCode: source.slotCode, lot: source.lot, toSlotCode: destination.code };
  }
  return {
    ...base,
    sources: planLegs(plan, preferred).flatMap((component) =>
      component.legs.map((leg) => ({
        productId: component.productId,
        slotCode: leg.slotCode,
        lot: leg.lot,
        quantity: leg.quantity,
      }))
    ),
    toSlotCode: destination.code,
    ...(kit.lot_controlled ? { lot: kitLot.lot.trim() } : {}),
    ...(kit.expiry_controlled ? { expiryDate: kitLot.expiryDate } : {}),
  };
};

/** The body of `POST kits/<id>/assemble/`. A source without a lot goes with `""`. */
export const buildAssembleBody = (arg, key) => ({
  quantity: Number(arg.quantity),
  sources: (arg.sources || []).map((source) => ({
    product_id: source.productId,
    slot_code: source.slotCode,
    lot: source.lot || '',
    quantity: Number(source.quantity),
  })),
  to_slot_code: arg.toSlotCode,
  ...(arg.lot != null ? { lot: arg.lot } : {}),
  ...(arg.expiryDate ? { expiry_date: arg.expiryDate } : {}),
  idempotency_key: key,
});

/** The body of `POST kits/<id>/disassemble/`. Kits without a lot come out of `""`. */
export const buildDisassembleBody = (arg, key) => ({
  quantity: Number(arg.quantity),
  from_slot_code: arg.fromSlotCode,
  lot: arg.lot || '',
  to_slot_code: arg.toSlotCode,
  idempotency_key: key,
});

/** Whether a write's argument is whole enough to send. */
export const isKitWriteArg = (arg) =>
  Boolean(arg?.kitId && Number(arg.quantity) > 0 && arg.toSlotCode && (arg.fromSlotCode || Array.isArray(arg.sources)));

/** Whether the visit is working something out, or holds a write whose answer was lost. */
export const isKitBusy = (visit) => Boolean(visit.pending || visit.lastWrite || visit.scanning);

/** A source in words: «4 POL-1 de A-01 · lote L1». */
export const sourceText = (sku, leg) =>
  [`${countText(leg.quantity, sku)} de ${leg.slotCode}`, leg.lot ? `lote ${leg.lot}` : null].filter(Boolean).join(' · ');

/** A place a component (or the kits) can come from, as a choice in «Cambiar». */
export const sourceOption = (option, suggestedKey) => ({
  id: sourceKeyOf(option),
  title: option.slot_name && option.slot_name !== option.slot_code ? `${option.slot_code} · ${option.slot_name}` : option.slot_code,
  subtitle: [
    option.lot ? `lote ${option.lot}` : null,
    option.expiry_date ? `vence ${option.expiry_date}` : null,
    `${formatQuantity(option.in_slot)} en la ubicación`,
  ]
    .filter(Boolean)
    .join(' · '),
  badge: sourceKeyOf(option) === suggestedKey ? 'Sugerida' : null,
});

/** A kit as a choice, when a code is more than one. */
export const kitOption = (row) => ({
  id: row.id,
  title: `${row.sku} — ${row.name}`,
  subtitle: [row.company?.name, kitRowCaption(row)].filter(Boolean).join(' · '),
  badge: null,
});

/** «Ver de dónde sale»'s next: «Ya los junté», «Ya los saqué». */
export const sourcesDoneLabel = (mode) => (isDisassembly(mode) ? 'Ya los saqué' : 'Ya los junté');

/** «Armar 5 KIT-1», «Desarmar 3 KIT-1». */
export const kitActionText = (mode, quantity, sku) => `${KIT_WORK_LABELS[mode]} ${countText(quantity, sku)}`;

/** The primary of the destination step: «Armar 5 KIT-1 en B-02». */
export const kitSubmitLabel = ({ mode, quantity, kit, destination }) =>
  `${kitActionText(mode, quantity, kitName(kit))} en ${destination?.code}`;

/** What the confirmation says goes where. */
export const kitConfirmMessage = (mode, destination) =>
  isDisassembly(mode) ? `Los componentes quedan en ${destination?.code}.` : `Quedan en ${destination?.code}.`;

/** «Armar más KIT-1», «Desarmar más KIT-1». */
export const kitAgainLabel = (mode, sku) => `${KIT_WORK_LABELS[mode]} más ${sku}`;

/** What a kit gives when taken apart: «6 POL-1 · 3 GOR-1». */
export const givesText = (plan) => (plan?.gives || []).map((item) => countText(item.quantity, item.sku)).join(' · ');

/** The band once the server registered it: «Armaste 5 KIT-1 en B-02.», or a retry that had gone through. */
export const kitWriteMessage = (kind, payload, arg) => {
  const count = countText(arg.quantity, arg.kitSku);
  if (isDisassembly(kind)) {
    return {
      tone: 'success',
      message: payload?.replayed
        ? `${count} ya ${plural(arg.quantity, 'estaba desarmado', 'estaban desarmados')}: no se ${plural(arg.quantity, 'desarmó', 'desarmaron')} dos veces.`
        : `Desarmaste ${count} en ${arg.toSlotCode}.`,
    };
  }
  return {
    tone: 'success',
    message: payload?.replayed
      ? `${count} ya ${plural(arg.quantity, 'estaba armado', 'estaban armados')}: no se ${plural(arg.quantity, 'armó', 'armaron')} dos veces.`
      : `Armaste ${count} en ${arg.toSlotCode}.`,
  };
};

/** Whether a 412 leaves the quantity out of reach: the visit goes back to it. */
export const staleNeedsQuantity = (error, quantity) =>
  error?.maxQuantity != null && milli(quantity) > milli(error.maxQuantity);

/**
 * The band of a 412: the server's words and what to do now. «En P-01 ya no
 * hay 2 de POL-1. Revisa de dónde sale: lo volvimos a calcular.», «Ya no
 * alcanza para armar 4 de KIT-1. Ahora puedes armar hasta 3.»
 */
export const kitStaleSignal = (kind, error, arg) => {
  const verb = isDisassembly(kind) ? 'desarmar' : 'armar';
  const max = Number(error?.maxQuantity) || 0;
  let next = 'Revisa de dónde sale: lo volvimos a calcular.';
  if (staleNeedsQuantity(error, arg.quantity)) {
    next = max > 0 ? `Ahora puedes ${verb} hasta ${formatQuantity(max)}.` : `Ahora no alcanza para ${verb} ninguno.`;
  }
  return { tone: 'warning', message: `${asSentence(error?.message)} ${next}`.trim() };
};

/** «5 KIT-1 armados en B-02», «3 KIT-1 desarmados en A-01». */
export const kitResultTitle = ({ kind, quantity, sku, toSlotCode }) => {
  const done = isDisassembly(kind) ? plural(quantity, 'desarmado', 'desarmados') : plural(quantity, 'armado', 'armados');
  return `${countText(quantity, sku)} ${done} en ${toSlotCode}`;
};

/** A leg of the ledger in words: «4 POL-1 de A-01 · lote L1» going out, «5 KIT-1 en B-02» coming in. */
export const legText = (leg) => {
  const amount = Number(leg.quantity) || 0;
  const where = amount < 0 ? `de ${leg.slot_code}` : `en ${leg.slot_code}`;
  return [`${countText(Math.abs(amount), leg.sku)} ${where}`, leg.lot ? `lote ${leg.lot}` : null].filter(Boolean).join(' · ');
};

/** The legs that went out, and those that came in: the ledger signs them. */
export const legsOut = (legs) => (legs || []).filter((leg) => Number(leg.quantity) < 0);
export const legsIn = (legs) => (legs || []).filter((leg) => Number(leg.quantity) > 0);
